import React, { useState } from "react";
import {
  Container,
  Paper,
  Title,
  Textarea,
  Select,
  Button,
  Stack,
  Group,
  TextInput,
  NumberInput,
  ActionIcon,
  Divider,
  Badge,
} from "@mantine/core";
import { IconArrowLeft, IconPlus, IconTrash } from "@tabler/icons-react";
import { useNavigate } from "react-router-dom";
import { useCreateRequisition } from "../hooks/useRequisitions";
import { notifications } from "@mantine/notifications";

interface RequisitionItem {
  id: string;
  itemName: string;
  description?: string;
  quantity: number;
  unit: string;
  estimatedPrice?: number;
  order: number;
}

const DEFAULT_UNITS = [
  { value: "units", label: "Units" },
  { value: "pieces", label: "Pieces" },
  { value: "kg", label: "Kilograms" },
  { value: "liters", label: "Liters" },
  { value: "hours", label: "Hours" },
  { value: "days", label: "Days" },
  { value: "meters", label: "Meters" },
  { value: "sets", label: "Sets" },
];

export function RequisitionForm() {
  const navigate = useNavigate();
  const createRequisition = useCreateRequisition();

  const [formData, setFormData] = useState({
    title: "",
    description: "",
    department: "",
    category: "" as "GOODS" | "SERVICES" | "",
    priority: "MEDIUM" as "LOW" | "MEDIUM" | "HIGH" | "URGENT",
  });

  const [items, setItems] = useState<RequisitionItem[]>([]);

  const addItem = () => {
    const newItem: RequisitionItem = {
      id: Date.now().toString(),
      itemName: "",
      description: "",
      quantity: 1,
      unit: "units",
      order: items.length + 1,
    };
    setItems([...items, newItem]);
  };

  const removeItem = (id: string) => {
    setItems(items.filter((item) => item.id !== id));
  };

  const updateItem = (id: string, field: keyof RequisitionItem, value: any) => {
    setItems(
      items.map((item) => (item.id === id ? { ...item, [field]: value } : item))
    );
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!formData.title || !formData.description || !formData.department || !formData.category) {
      notifications.show({
        title: "Validation Error",
        message: "Please fill in all required fields",
        color: "red",
      });
      return;
    }

    if (items.length === 0) {
      notifications.show({
        title: "Validation Error",
        message: "Please add at least one item",
        color: "red",
      });
      return;
    }

    const invalidItems = items.filter((item) => !item.itemName.trim());
    if (invalidItems.length > 0) {
      notifications.show({
        title: "Validation Error",
        message: "Please provide item names for all items",
        color: "red",
      });
      return;
    }

    createRequisition.mutate(
      {
        title: formData.title,
        description: formData.description,
        department: formData.department,
        category: formData.category as "GOODS" | "SERVICES",
        priority: formData.priority,
        items: items.map((item) => ({
          itemName: item.itemName,
          description: item.description,
          quantity: item.quantity,
          unit: item.unit,
          estimatedPrice: item.estimatedPrice,
          order: item.order,
        })),
      },
      {
        onSuccess: () => {
          navigate("/requisitions");
        },
      }
    );
  };

  return (
    <Container size="xl" py="xl">
      <Group mb="xl">
        <ActionIcon variant="subtle" onClick={() => navigate("/requisitions")}>
          <IconArrowLeft size={20} />
        </ActionIcon>
        <Title order={2}>Create Requisition</Title>
      </Group>

      <form onSubmit={handleSubmit}>
        <Stack gap="md">
          <Paper shadow="sm" p="md">
            <Stack gap="md">
              <TextInput
                label="Title"
                placeholder="Enter requisition title"
                required
                value={formData.title}
                onChange={(e) =>
                  setFormData({ ...formData, title: e.target.value })
                }
              />

              <Textarea
                label="Description"
                placeholder="Enter requisition description"
                required
                minRows={3}
                value={formData.description}
                onChange={(e) =>
                  setFormData({ ...formData, description: e.target.value })
                }
              />

              <Group grow>
                <TextInput
                  label="Department"
                  placeholder="Enter department name"
                  required
                  value={formData.department}
                  onChange={(e) =>
                    setFormData({ ...formData, department: e.target.value })
                  }
                />

                <Select
                  label="Category"
                  placeholder="Select category"
                  required
                  data={[
                    { value: "GOODS", label: "Goods" },
                    { value: "SERVICES", label: "Services" },
                  ]}
                  value={formData.category}
                  onChange={(value) =>
                    setFormData({
                      ...formData,
                      category: (value as "GOODS" | "SERVICES") || "",
                    })
                  }
                />

                <Select
                  label="Priority"
                  data={[
                    { value: "LOW", label: "Low" },
                    { value: "MEDIUM", label: "Medium" },
                    { value: "HIGH", label: "High" },
                    { value: "URGENT", label: "Urgent" },
                  ]}
                  value={formData.priority}
                  onChange={(value) =>
                    setFormData({
                      ...formData,
                      priority: (value as "LOW" | "MEDIUM" | "HIGH" | "URGENT") || "MEDIUM",
                    })
                  }
                />
              </Group>
            </Stack>
          </Paper>

          <Paper shadow="sm" p="md">
            <Group justify="space-between" mb="md">
              <Title order={4}>Items</Title>
              <Button
                leftSection={<IconPlus size={16} />}
                onClick={addItem}
                variant="light"
              >
                Add Item
              </Button>
            </Group>

            {items.length === 0 ? (
              <Stack align="center" py="xl">
                <p>No items added yet. Click "Add Item" to get started.</p>
              </Stack>
            ) : (
              <Stack gap="md">
                {items.map((item, index) => (
                  <Paper key={item.id} p="md" withBorder>
                    <Group justify="space-between" mb="sm">
                      <Badge>Item {index + 1}</Badge>
                      <ActionIcon
                        color="red"
                        variant="subtle"
                        onClick={() => removeItem(item.id)}
                      >
                        <IconTrash size={16} />
                      </ActionIcon>
                    </Group>
                    <Stack gap="sm">
                      <TextInput
                        label="Item Name"
                        placeholder="Enter item name"
                        required
                        value={item.itemName}
                        onChange={(e) =>
                          updateItem(item.id, "itemName", e.target.value)
                        }
                      />
                      <Textarea
                        label="Description"
                        placeholder="Enter item description (optional)"
                        value={item.description}
                        onChange={(e) =>
                          updateItem(item.id, "description", e.target.value)
                        }
                      />
                      <Group grow>
                        <NumberInput
                          label="Quantity"
                          placeholder="Enter quantity"
                          required
                          min={1}
                          value={item.quantity}
                          onChange={(value) =>
                            updateItem(item.id, "quantity", Number(value) || 1)
                          }
                        />
                        <Select
                          label="Unit"
                          data={DEFAULT_UNITS}
                          value={item.unit}
                          onChange={(value) =>
                            updateItem(item.id, "unit", value || "units")
                          }
                        />
                        <NumberInput
                          label="Estimated Price (Optional)"
                          placeholder="Enter estimated price"
                          min={0}
                          decimalScale={2}
                          value={item.estimatedPrice}
                          onChange={(value) =>
                            updateItem(item.id, "estimatedPrice", value ? Number(value) : undefined)
                          }
                        />
                      </Group>
                    </Stack>
                  </Paper>
                ))}
              </Stack>
            )}
          </Paper>

          <Group justify="flex-end">
            <Button variant="subtle" onClick={() => navigate("/requisitions")}>
              Cancel
            </Button>
            <Button
              type="submit"
              loading={createRequisition.isPending}
              disabled={items.length === 0}
            >
              Create Requisition
            </Button>
          </Group>
        </Stack>
      </form>
    </Container>
  );
}

